/**
 * (owner, Sep 25) What a billboard shows: a brand of this world (content/brands), or — on `share` % of the boards — one of the
 * world's own ads from the admin panel (server/ads.ts; owner: "a setting that dictates what percentage of the billboards should switch
 * to the custom advertisement"). Both picked by the board's position, so every player sees the same poster on the same board, and a
 * higher share only adds boards. Pure — the renderer asks, the tests check.
 */
import type { Brand, RoadClass, ZoneName } from '../content/types';
import { hash, hash01 } from '../world/rng';

/** (owner, Sep 25 follow-up) where an ad may go: a zone of the town, or `motorway` — the big boards beside a highway */
export type AdWhere = 'downtown' | 'suburb' | 'industrial' | 'rural' | 'motorway';
export const AD_WHERE: AdWhere[] = ['downtown', 'suburb', 'industrial', 'rural', 'motorway'];
/** a world's own ad (the admin panel's Ads card): the words, two colours, a brand's logo (a brand id) or a picture (a file the world serves at /ads/<file>), a link line */
export interface CustomAd { id: string; title: string; text?: string; color: string; color2: string; logo?: string; image?: string; link?: string; /** false = switched off on the panel (kept, not shown) */ on?: boolean; /** how often it is picked against the others (1–10, default 1) */ weight?: number; /** only on boards there (none = everywhere) */ where?: AdWhere[]; /** bumped on every edit (the faces are cached by it) */ v: number }
/** the world's ads as the players get them (`{t:'ads'}` on the wire), the share of boards that show one (0–100), and how long a digital board shows each (seconds) */
export interface CustomAds { share: number; ads: CustomAd[]; cycle?: number }
/** a digital board's default: a new ad every this many seconds */
export const AD_CYCLE = 8;
/** what the renderer knows about a board: the zone it stands in, the road beside it (a big roadside board), whether it is a screen, the world's clock */
export interface BoardSpot { zone?: ZoneName; road?: RoadClass; digital?: boolean; clock?: number }

/** does the board at `x, y` switch to a custom ad at this share — the same boards for everyone, and every board of a lower share among a higher one's */
export function customShown(seed: number, x: number, y: number, share: number): boolean { return share > 0 && hash01(seed + 97, x, y) * 100 < share; }
/** may this ad go on a board here */
export function adFits(a: CustomAd, spot: BoardSpot = {}): boolean {
  if (a.on === false) return false;
  if (!a.where?.length) return true;
  return a.where.some((w) => (w === 'motorway' ? spot.road === 'highway' : w === spot.zone));
}
/** one of `list` by weight, `u` in [0, 1) */
export function pickWeighted<T extends { weight?: number }>(list: T[], u: number): T {
  let total = 0; for (const a of list) total += Math.max(1, a.weight ?? 1);
  let r = u * total; for (const a of list) { r -= Math.max(1, a.weight ?? 1); if (r < 0) return a; }
  return list[list.length - 1];
}
/**
 * What the board at `x, y` (whole tiles) shows: a custom ad when its spot switches and an ad fits there, else its brand. A digital
 * board (owner, Sep 25: "cycles through the custom ads every N seconds") changes every `cycle` seconds of the world's clock — the server's,
 * so every player sees the same ad at the same moment; a digital board that did not switch cycles through the brands.
 */
export function boardAd(seed: number, x: number, y: number, brands: Brand[], custom?: CustomAds, spot: BoardSpot = {}): { brand?: Brand; custom?: CustomAd } {
  const slot = spot.digital ? Math.floor((spot.clock ?? 0) / Math.max(1, custom?.cycle ?? AD_CYCLE)) : -1;
  if (custom && custom.share > 0 && customShown(seed, x, y, custom.share)) {
    const fit = custom.ads.filter((a) => adFits(a, spot));
    if (fit.length) return { custom: pickWeighted(fit, slot < 0 ? hash01(seed + 98, x, y) : hash01(seed + 99, x, y, slot)) };
  }
  if (slot >= 0 && brands.length) return { brand: brands[hash(seed + 100, x, y, slot) % brands.length] };
  return { brand: brandAt(seed, x, y, brands) };
}

/** the brand on the board at `x, y` (whole tiles) — the formula the small boards always used, so they kept their posters */
export function brandAt(seed: number, x: number, y: number, brands: Brand[]): Brand | undefined {
  if (!brands.length) return undefined;
  return brands[Math.floor(hash01(seed + 77, x * 31 + y) * brands.length) % brands.length];
}
